import {
  getOrderStats,
  getFoodAllergens,
  getFoodIngredients,
  getFoodItemAdditions,
  getFoodPreferences,
  getItemCategories,
  getMenuEntries,
  getStoreBySlug,
  getStoreManager,
  getStoreTables,
  getStorePrinterConfigs,
  getOrders,
  getServiceTimes,
  getCurrentServiceTime,
  getNextStartServiceTime,
  getPosIntegrations,
  getPosIntegrationItemDataMappings,
  getPosIntegrationItemAdditionMappings,
  getPosIntegrationTableMappings,
  getStores,
  getAllStoresStats,
  getFoodAdditives,
  getStoreDiscountCodes,
  getAdditionGroups,
  getPaymentTerminals,
  getStorePickupStations,
  getKiosks,
  getConnectors,
  getAreas,
  getInvoices,
  getLogs,
  getPosIntegrationItemAdditionGroupMappings,
  getMenuItems,
  getItemDatas,
  getBridges,
  getAllBridges,
  getStoreTablesPublic,
  getSystemDiscountCodes,
  getOrderConfigPublic,
  updateOrderConfig,
  getDailyLimits,
} from '../../utility/sb-api/sb-api.service';
import {
  getOrderItemHash,
  getFirstDateOfMonth,
  getCartOrderConditionViolations,
  getEffectiveRequestedAt,
  isTakeawayOrderClass,
  getLastDateOfMonth,
  getLocaleHierarchy,
  getOrderItemTotal,
} from '../../utility/util/util.service';
import {
  ORDER_STATUS_PAID,
  ORDER_STATUS_PENDING,
} from '../../utility/constants';
import { notification } from '../../utility/notification/notification.service';

// fetch all relevant data combined on app entry
export const fetchOnAppEntry = () => (dispatch) => {
  dispatch(fetchFoodAllergens());
  dispatch(fetchFoodAdditives());
  dispatch(fetchFoodPreferences());
  dispatch(fetchFoodIngredients());
};

let isAdminFetchInProgress = false;

export const fetchOnAdminAppEntry = () => (dispatch) => {
  if (isAdminFetchInProgress) return;
  isAdminFetchInProgress = true;

  Promise.all([
    dispatch(fetchStores()),
    dispatch(fetchAllStoresStats()),
    dispatch(fetchAllBridges()),
    dispatch(fetchSystemDiscountCodes()),
  ]).finally(() => {
    isAdminFetchInProgress = false;
  });
};

// fetch all relevant data combined on store entry / store change
export const fetchOnStoreEntry = (store, isAdminMode = false) => async (
  dispatch,
) => {
  const menuId = store?.menu?.id;
  const storeId = store?.id;

  if (!menuId) {
    console.error('fetchOnStoreEntry: store.menu.id is missing', store);
    return;
  }

  // Wait for all menu-related fetches
  await Promise.all([
    dispatch(fetchMenuItems(isAdminMode, menuId, undefined, storeId)),
    dispatch(fetchItemDatas(isAdminMode, menuId, undefined, storeId)),
    dispatch(fetchFoodItemAdditions(isAdminMode, menuId, undefined, storeId)),
    dispatch(fetchItemCategories(menuId, isAdminMode, undefined, storeId)),
    dispatch(fetchDailyLimitsAction(menuId)),
  ]);

  await Promise.all([
    dispatch(fetchAdditionGroups(isAdminMode, menuId, undefined, storeId)),
  ]);

  // After all menu-related fetches are done, fetch menu entries
  dispatch(fetchMenuEntries(menuId, isAdminMode, undefined, storeId));

  if (isAdminMode) {
    dispatch(fetchOrderStats());
    dispatch(fetchTables());
    dispatch(fetchDiscountCodes());
    dispatch(fetchPrinterConfigs());
    dispatch(fetchPickupStations());
    dispatch(fetchKiosks());
    dispatch(fetchPaymentTerminals());
    dispatch(fetchStoreOrders());
    dispatch(
      fetchPosIntegrationTableMappings(
        store?.id,
        store?.inhouseOrderConfig?.posIntegration?.id,
      ),
    );
    /*dispatch(fetchPosIntegrations());*/
    dispatch(fetchConnectors());
    //dispatch(fetchLogs(store.id));
    dispatch(fetchAreas());
    dispatch(fetchInvoices());
    dispatch(fetchBridges());
  } else {
    if (store?.merchantProfile?.enableSelfServiceTableSelection) {
      dispatch(fetchTablesPublic(store.id));
    }
  }
};

export const setStore = (store, flush = false) => (dispatch) => {
  dispatch({
    type: flush ? 'SET_STORE_AND_FLUSH' : 'SET_STORE',
    payload: store,
  });
};

export const setMerchantProfile = (merchantProfile) => (dispatch) => {
  dispatch({
    type: 'SET_MERCHANT_PROFILE',
    payload: merchantProfile,
  });
};

// Set a specific order config on the store
export const setOrderConfig = (configType, config) => (dispatch) => {
  dispatch({
    type: 'SET_ORDER_CONFIG',
    payload: { configType, config },
  });
};

// Fetch a specific order config by ID (public endpoint)
export const fetchOrderConfig = (storeId, orderConfigId, configType) => (
  dispatch,
) => {
  return getOrderConfigPublic(storeId, orderConfigId)
    .then((config) => {
      dispatch(setOrderConfig(configType, config));
      return config;
    })
    .catch((err) => {
      console.error(`Failed to fetch ${configType}:`, err);
      throw err;
    });
};

// Fetch all order configs for a store (for public/guest access)
// This fetches each config individually using the IDs from the store object
export const fetchAllOrderConfigs = (store) => async (dispatch) => {
  const storeId = store?.id;
  if (!storeId) return;

  const configTypes = [
    { key: 'inhouseOrderConfig', id: store.inhouseOrderConfig },
    { key: 'pickupOrderConfig', id: store.pickupOrderConfig },
    { key: 'deliveryOrderConfig', id: store.deliveryOrderConfig },
    { key: 'cateringOrderConfig', id: store.cateringOrderConfig },
    { key: 'selfServiceOrderConfig', id: store.selfServiceOrderConfig },
  ];

  const fetchPromises = configTypes
    .filter(({ id }) => id) // Only fetch configs that have an ID
    .map(({ key, id }) => {
      // Handle both string ID and object with id property
      const configId = typeof id === 'string' ? id : id?.id;
      if (!configId) return Promise.resolve();
      return dispatch(fetchOrderConfig(storeId, configId, key));
    });

  return Promise.all(fetchPromises);
};

// Update an order config (manager endpoint)
export const patchOrderConfig = (
  storeId,
  orderConfigId,
  configType,
  payload,
) => (dispatch) => {
  return updateOrderConfig(storeId, orderConfigId, payload).then((config) => {
    dispatch(setOrderConfig(configType, config));
    return config;
  });
};

export const fetchStore = (
  slug,
  isManagerMode,
  flush = false,
  onSuccessCallback = () => undefined,
) => (dispatch) => {
  // Use role-based endpoints:
  // - Manager/store-owner: manager/stores
  // - Public/guest: public/stores/by-slug/:slug
  const fetchPromise = isManagerMode ? getStoreManager() : getStoreBySlug(slug);
  fetchPromise
    .then((store) => {
      dispatch(setStore(store, flush));
      // For public access, fetch order configs separately since they come as IDs
      if (!isManagerMode && store) {
        dispatch(fetchAllOrderConfigs(store));
      }
      return store;
    })
    .then(() => onSuccessCallback());
};

export const fetchMenuEntries = (
  menuId,
  isAdminMode,
  onSuccessCallback = () => undefined,
  storeId = null,
) => (dispatch) => {
  return getMenuEntries(menuId, isAdminMode, null, storeId)
    .then((menuEntries) =>
      dispatch({
        type: 'SET_MENU_ENTRIES',
        payload: menuEntries,
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchMenuItems = (
  isAdminMode,
  menuId,
  onSuccessCallback = () => undefined,
  storeId = null,
) => (dispatch) => {
  return getMenuItems(isAdminMode, menuId, null, storeId) // Return the promise here
    .then((items) =>
      dispatch({
        type: 'SET_MENU_ITEMS',
        payload: items,
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchItemDatas = (
  isAdminMode,
  menuId,
  onSuccessCallback = () => undefined,
  storeId = null,
) => (dispatch) => {
  return getItemDatas(isAdminMode, menuId, null, storeId) // Return the promise
    .then((itemDatas) =>
      dispatch({
        type: 'SET_MENU_ITEM_DATAS',
        payload: itemDatas,
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchDailyLimitsAction = (menuId) => (dispatch) => {
  return getDailyLimits(menuId)
    .then((dailyLimits) =>
      dispatch({
        type: 'SET_DAILY_LIMITS',
        payload: dailyLimits,
      }),
    )
    .catch(() => {}); // silently fail
};

export const fetchItemCategories = (
  menuId,
  isAdminMode,
  onSuccessCallback = () => undefined,
  storeId = null,
) => (dispatch) => {
  return getItemCategories(menuId, isAdminMode, null, storeId)
    .then((itemCategories) =>
      dispatch({
        type: 'SET_ITEM_CATEGORIES',
        payload: itemCategories,
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchFoodAllergens = (onSuccessCallback = () => undefined) => (
  dispatch,
) => {
  getFoodAllergens()
    .then((foodAllergens) =>
      dispatch({
        type: 'SET_FOOD_ALLERGENS',
        payload: foodAllergens.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        ),
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchFoodAdditives = (onSuccessCallback = () => undefined) => (
  dispatch,
) => {
  getFoodAdditives()
    .then((foodAdditives) =>
      dispatch({
        type: 'SET_FOOD_ADDITIVES',
        payload: foodAdditives.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        ),
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchFoodPreferences = (onSuccessCallback = () => undefined) => (
  dispatch,
) => {
  getFoodPreferences()
    .then((foodPreferences) =>
      dispatch({
        type: 'SET_FOOD_PREFERENCES',
        payload: foodPreferences.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        ),
      }),
    )
    .then(() => onSuccessCallback());
};

export const fetchFoodIngredients = (onSuccessCallback = () => undefined) => (
  dispatch,
) => {
  getFoodIngredients()
    .then((foodIngredients) =>
      dispatch({
        type: 'SET_FOOD_INGREDIENTS',
        payload: foodIngredients.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        ),
      }),
    )
    .then(() => onSuccessCallback());
};

// admin only
export const fetchFoodItemAdditions = (
  isAdminMode,
  menuId,
  onSuccessCallback = () => undefined,
  storeId = null,
) => (dispatch) => {
  return getFoodItemAdditions(isAdminMode, menuId, null, storeId)
    .then((foodItemAdditions) =>
      dispatch({
        type: 'SET_FOOD_ITEM_ADDITIONS',
        payload: foodItemAdditions,
      }),
    )
    .then(() => onSuccessCallback());
};

export const setCart = (cart) => (dispatch) => {
  dispatch({
    type: 'SET_CART',
    payload: cart,
  });
};

// Returns the message id explaining why the resulting cart would be rejected,
// or null when the add/update may go through.
//
// Blocking means a breach the customer cannot resolve by adding more: an upper
// bound (maxQty/maxTotal), an item unavailable for the current order class, or
// — once a fulfillment time has been chosen — an item whose service times do
// not cover it.
//
// A service-time breach is NOT blocking while a pre-order (pickup/delivery/
// catering) has no time chosen yet: the customer picks it later, exactly as the
// backend does, which evaluates conditions against
// `pickupRequestedAt ?? deliveryRequestedAt ?? now`. Lower bounds
// (minQty/minTotal) are never blocking here — the cart must be allowed to grow
// toward them — and are enforced at checkout by getCartOrderConditionViolations.
const cartAddViolationMessage = (cart, orderType, requestedAt) => {
  // Immediate order types never carry a requested time, so their service-time
  // windows are checked against now and a miss is final.
  const blockTimeMismatch = !!requestedAt || !isTakeawayOrderClass(orderType);
  const violations = getCartOrderConditionViolations(
    cart,
    orderType,
    requestedAt ?? new Date(),
  ).flatMap(({ violations }) => violations);
  if (
    !violations.some(
      ({ type }) =>
        type === 'maxQty' ||
        type === 'maxTotal' ||
        type === 'classMismatch' ||
        (type === 'serviceTimeMismatch' && blockTimeMismatch),
    )
  )
    return null;

  // If the only reason is that the chosen time misses the windows, say so —
  // that is fixable by picking another time, and the generic "item
  // combination" wording would send the customer looking for a bad topping.
  return (
    requestedAt &&
    violations.every(
      ({ type }) => type === 'serviceTimeMismatch' || type === 'minTotal',
    )
  )
    ? 'itemUnavailableAtRequestedTimeError'
    : 'addToCartOrderConditionsError';
};

export const updateCartItem = (
  cart,
  targetHash,
  updateFunc = (item) => item,
  checkout,
) => (dispatch) => {
  const existingOrderItem = cart?.find(({ hash }) => targetHash === hash);

  if (!existingOrderItem) {
    throw new Error(`Could not find OrderItem with hash "${targetHash}"`);
  }

  const updatedOrderItem = {
    ...existingOrderItem,
    ...updateFunc(existingOrderItem),
  };
  const itemData = existingOrderItem.itemData;
  const updatedCart = [
    ...cart.filter(({ hash }) => targetHash !== hash),
    updatedOrderItem,
  ]
    .filter(({ qty }) => qty > 0) // remove items with qty = 0;
    .sort((a, b) => a.hash.localeCompare(b.hash)); // sort by hash

  // Dispatch the updated cart unless it would breach an unresolvable
  // OrderCondition (qty=0 removals are always allowed). Otherwise, show an
  // Error Notification.
  const violationMessage =
    updatedOrderItem?.qty === 0
      ? null
      : cartAddViolationMessage(
          updatedCart,
          checkout?.orderType,
          getEffectiveRequestedAt(checkout),
        );
  if (!violationMessage) {
    dispatch(setCart(updatedCart));
  } else {
    notification('warning', violationMessage);
    console.error('Could not find OrderItem with hash ' + targetHash);
  }
};

export const addCartItem = (cart, itemData, additions = [], qty, orderType) => (
  dispatch,
  getState,
) => {
  const targetHash = getOrderItemHash({ itemData, additions });
  const existingOrderItem = cart?.find(({ hash }) => targetHash === hash);
  const newOrderItem = {
    hash: targetHash,
    qty: qty,
    itemData: itemData,
    additions: additions,
    course: itemData.defaultCourse,
    type: 'OrderItem',
  };

  // check if price is less than 0 - that's not allowed
  const price = getOrderItemTotal(orderType, newOrderItem);
  if (price < 0) {
    notification('warning', 'priceBelowZeroError');
    return;
  }

  const updatedCart = (existingOrderItem
    ? [
        ...cart.filter(({ hash }) => targetHash !== hash), // all other items
        { ...existingOrderItem, qty: existingOrderItem.qty + qty }, // increase qty
      ]
    : [
        ...(cart || []), // current cart (if any)
        newOrderItem,
      ]
  ).sort((a, b) => a.hash.localeCompare(b.hash)); // sort by hash

  // Dispatch the updated cart unless it would breach an unresolvable
  // OrderCondition. Lower bounds are validated at checkout. Otherwise, show an
  // Error Notification.
  const requestedAt = getEffectiveRequestedAt(getState().store?.checkout);
  const violationMessage = cartAddViolationMessage(
    updatedCart,
    orderType,
    requestedAt,
  );
  if (!violationMessage) {
    dispatch(setCart(updatedCart));
  } else {
    notification('warning', violationMessage);
  }
};

export const removeCartItem = (cart, targetHash) => (dispatch) => {
  const updatedCart = cart
    ?.filter(({ hash }) => targetHash !== hash) // all other items
    .sort((a, b) => a.hash.localeCompare(b.hash)); // sort by hash

  dispatch(setCart(updatedCart));
};

export const fetchOrderStats = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  const now = new Date();
  const firstDateOfPreviousMonth = getFirstDateOfMonth(now, -1);
  const firstDateOfCurrentMonth = getFirstDateOfMonth(now);
  const lastDateOfPreviousMonth = getLastDateOfMonth(now, -1);
  const lastDateOfCurrentMonth = getLastDateOfMonth(now);

  // v1 API filter format: array of strings "field||$op||value"
  const queryParamsThisMonth = {
    filter: [
      `createdAt||$gte||${firstDateOfCurrentMonth.toISOString()}`,
      `createdAt||$lte||${lastDateOfCurrentMonth.toISOString()}`,
    ],
  };
  const queryParamsLastMonth = {
    filter: [
      `createdAt||$gte||${firstDateOfPreviousMonth.toISOString()}`,
      `createdAt||$lte||${lastDateOfPreviousMonth.toISOString()}`,
    ],
  };

  Promise.all([
    getOrderStats(queryParamsThisMonth, storeId),
    getOrderStats(queryParamsLastMonth, storeId),
  ]).then(([thisMonthStats, lastMonthStats]) => {
    dispatch({
      type: 'SET_ORDER_STATS',
      payload: {
        thisMonth: thisMonthStats,
        lastMonth: lastMonthStats,
      },
    });
  });
};

export const fetchTablesPublic = (storeId) => (dispatch) => {
  getStoreTablesPublic(storeId).then((tables) =>
    dispatch({
      type: 'SET_TABLES',
      payload: tables,
    }),
  );
};

/* export const fetchTables = (
  recentTableCreatedAt,
  oldestTableCreatedAt,
  onSuccessCallback = () => undefined,
) => (dispatch) => {
  const limit = 25;

  const fetchData = (offset = 0) => {
    const queryParams = {
      filter: [
        ...(recentTableCreatedAt
          ? [
              ['createdAt', '$gt', recentTableCreatedAt],
              ['createdAt', '$ne', recentTableCreatedAt],
            ]
          : []),
        ...(oldestTableCreatedAt
          ? [['createdAt', '$lt', oldestTableCreatedAt]]
          : []),
      ],
      sort: [{ field: 'createdAt', order: 'DESC' }],
      limit: limit,
      offset: offset,
    };

    getStoreTables(queryParams).then((tables) => {
      if (tables.length > 0) {
        dispatch({
          type: 'SET_TABLES',
          payload: tables,
        });
        onSuccessCallback(tables);
        fetchData(25 + limit);
      }
    });
  };

  fetchData();
}; */

export const fetchTables = (
  recentTableCreatedAt,
  oldestTableCreatedAt,
  onSuccessCallback = () => undefined,
) => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  // v1 API filter format: array of strings "field||$op||value"
  const filters = [];
  if (recentTableCreatedAt) {
    filters.push(`createdAt||$gt||${recentTableCreatedAt}`);
  }
  if (oldestTableCreatedAt) {
    filters.push(`createdAt||$lt||${oldestTableCreatedAt}`);
  }
  const queryParams = {
    filter: filters.length > 0 ? filters : undefined,
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: 25,
  };
  getStoreTables(queryParams, storeId).then((tables) => {
    dispatch({
      type: oldestTableCreatedAt ? 'ADD_TABLES' : 'SET_TABLES',
      payload: tables,
    });
    onSuccessCallback(tables);
  });
};

export const fetchDiscountCodes = (
  recentDiscountCodesCreatedAt,
  oldestDiscountCodesCreatedAt,
  onSuccessCallback = () => undefined,
) => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getStoreDiscountCodes({}, storeId).then((discountCodes) => {
    dispatch({
      type: oldestDiscountCodesCreatedAt
        ? 'ADD_DISCOUNT_CODES'
        : 'SET_DISCOUNT_CODE',
      payload: discountCodes,
    });
    onSuccessCallback(discountCodes);
  });
};

export const fetchSystemDiscountCodes = (
  recentDiscountCodesCreatedAt,
  oldestDiscountCodesCreatedAt,
  onSuccessCallback = () => undefined,
) => (dispatch) => {
  getSystemDiscountCodes().then((discountCodes) => {
    dispatch({
      type: oldestDiscountCodesCreatedAt
        ? 'ADD_SYSTEM_DISCOUNT_CODES'
        : 'SET_SYSTEM_DISCOUNT_CODES',
      payload: discountCodes,
    });
    onSuccessCallback(discountCodes);
  });
};

export const setSpecificTable = (table) => (dispatch) => {
  dispatch({
    type: 'SET_SPECIFIC_TABLE',
    payload: table,
  });
};

export const setSpecificArea = (area) => (dispatch) => {
  dispatch({
    type: 'SET_SPECIFIC_AREA',
    payload: area,
  });
};

export const setSpecificDiscountCode = (discount) => (dispatch) => {
  dispatch({
    type: 'SET_SPECIFIC_DISCOUNT_CODE',
    payload: discount,
  });
};

export const fetchPrinterConfigs = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getStorePrinterConfigs({}, storeId).then((printerConfig) =>
    dispatch({
      type: 'SET_PRINTER_CONFIGS',
      payload: printerConfig,
    }),
  );
};

export const fetchConnectors = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getConnectors(storeId).then(({ data }) =>
    dispatch({
      type: 'SET_CONNECTORS',
      payload: data,
    }),
  );
};

export const fetchLogs = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getLogs(storeId).then(({ logs }) =>
    dispatch({
      type: 'SET_LOGS',
      payload: logs,
    }),
  );
};

export const setLogs = (logs) => (dispatch) => {
  dispatch({
    type: 'SET_LOGS',
    payload: logs,
  });
};

export const fetchPickupStations = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getStorePickupStations({}, storeId).then((pickupStations) =>
    dispatch({
      type: 'SET_PICKUP_STATIONS',
      payload: pickupStations,
    }),
  );
};

export const setCheckout = (checkout) => (dispatch) => {
  dispatch({
    type: 'SET_CHECKOUT',
    payload: checkout,
  });
};

export const setCheckoutBackup = (checkoutBackup) => (dispatch) => {
  dispatch({
    type: 'SET_CHECKOUT_BACKUP',
    payload: checkoutBackup,
  });
};

export const setOrderHash = (hash) => (dispatch) => {
  dispatch({
    type: 'SET_ORDER_HASH',
    payload: hash,
  });
};

export const setOrder = (order) => (dispatch) => {
  dispatch({
    type: 'SET_ORDER',
    payload: order,
  });
};

export const setTable = (table) => (dispatch) => {
  dispatch({
    type: 'SET_TABLE',
    payload: table,
  });
};

export const setKiosk = (kiosk) => (dispatch) => {
  dispatch({
    type: 'SET_KIOSK',
    payload: kiosk,
  });
};

export const setPickupStation = (pickupStation) => (dispatch) => {
  dispatch({
    type: 'SET_PICKUP_STATION',
    payload: pickupStation,
  });
};

export const addProcessedSocketEventId = (eventId) => (dispatch) => {
  dispatch({
    type: 'ADD_PROCESSED_SOCKET_EVENT_ID',
    payload: eventId,
  });
};

export const fetchStoreOrders = (
  recentOrderCreatedAt,
  oldestOrderCreatedAt,
  onSuccessCallback = () => undefined,
) => (dispatch, getState) => {
  // Get storeId from Redux state for manager endpoint
  const storeId = getState()?.store?.store?.id;

  // v1 API filter format: array of strings "field||$op||value"
  // No join - backend auto-includes relations for manager endpoints
  // No limit - use backend default (25)
  const filters = [
    `status||$notin||${ORDER_STATUS_PAID},${ORDER_STATUS_PENDING}`,
  ];

  if (recentOrderCreatedAt) {
    filters.push(`createdAt||$gt||${recentOrderCreatedAt}`);
  }
  if (oldestOrderCreatedAt) {
    filters.push(`createdAt||$lt||${oldestOrderCreatedAt}`);
  }

  const queryParams = {
    filter: filters,
    sort: [{ field: 'createdAt', order: 'DESC' }],
  };
  // Pass storeId to use manager/stores/{storeId}/orders endpoint
  getOrders(null, queryParams, true, storeId).then((orders) => {
    dispatch({
      type:
        recentOrderCreatedAt || oldestOrderCreatedAt
          ? 'SET_STORE_SPECIFIC_ORDERS'
          : 'SET_STORE_ORDERS',
      payload: orders,
    });
    onSuccessCallback(orders);
  });
};

export const fetchServiceTimes = (storeId) => (dispatch) => {
  getServiceTimes(storeId).then((serviceTimes) =>
    dispatch({
      type: 'SET_SERVICE_TIMES',
      payload: serviceTimes,
    }),
  );
};

export const fetchCurrentServiceTime = (storeId) => (dispatch) => {
  getCurrentServiceTime(storeId)
    .then((serviceTime) =>
      dispatch({
        type: 'SET_CURRENT_SERVICE_TIME',
        payload: serviceTime,
      }),
    )
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        dispatch({
          type: 'SET_CURRENT_SERVICE_TIME',
          payload: null,
        });
      }
    });
};

export const fetchNextServiceTime = (storeId) => (dispatch) => {
  getNextStartServiceTime(storeId).then((serviceTime) =>
    dispatch({
      type: 'SET_NEXT_SERVICE_TIME',
      payload: serviceTime,
    }),
  );
};

export const setTableCart = (cart) => (dispatch) => {
  dispatch({
    type: 'SET_TABLE_CART',
    payload: cart,
  });
};

export const setSharedCarts = (sharedCarts) => (dispatch) => {
  dispatch({
    type: 'SET_TABLE_CARTS',
    payload: sharedCarts,
  });
};

export const addSearchStringsToMenuEntriesData = (menuEntries, locale) => (
  dispatch,
) => {
  const preperedMenuEntries = extractData(menuEntries)?.map((data) => ({
    ...data,
    availableAt:
      menuEntries?.find((menuEntry) =>
        menuEntry?.itemEntry?.data?.some((item) => item?.id === data?.id),
      )?.itemEntry?.availableAt || [],
    searchString: getLocaleHierarchy(locale)
      .map((loc) => {
        const nameObj = data?.name;
        const descObj = data?.description;
        const locKey = loc.toLowerCase();
        // Support both new format { name: { de: "..." } } and legacy { nameDE: "..." }
        const name =
          (nameObj && typeof nameObj === 'object'
            ? nameObj[locKey]
            : data?.[`name${loc}`]) || '';
        const desc =
          data?.type === 'FoodItemData'
            ? (descObj && typeof descObj === 'object'
                ? descObj[locKey]
                : data?.[`description${loc}`]) || ''
            : '';
        return `${name}¦${desc}`;
      })
      .join('¦'),
  }));

  dispatch({
    type: 'SET_MENU_ENTRIES_DATA',
    payload: preperedMenuEntries,
  });
};

export const extractData = (menuEntries) =>
  menuEntries
    ?.map((menuEntry) =>
      menuEntry.type === 'CategoryMenuEntry'
        ? menuEntry.categoryEntry
        : menuEntry.type === 'InfoMenuEntry'
        ? menuEntry
        : menuEntry.itemEntry?.data,
    )
    .filter(Boolean)
    .flat();

export const fetchPosIntegrations = (storeId) => (dispatch) => {
  getPosIntegrations(storeId).then(({ data }) => {
    dispatch({
      type: 'SET_POS_INTEGRATIONS',
      payload: data,
    });
  });
};
export const setStoreSpecificOrder = (order) => (dispatch) => {
  dispatch({
    type: 'SET_STORE_SPECIFIC_ORDER',
    payload: order,
  });
};

export const setAvailableOrderTypes = (orderTypes) => (dispatch) => {
  dispatch({
    type: 'SET_AVAILABLE_ORDER_TYPES',
    payload: orderTypes,
  });
};

export const fetchPosIntegrationItemDataMappings = (
  storeId,
  posIntegrationId,
) => (dispatch) => {
  getPosIntegrationItemDataMappings(storeId, posIntegrationId).then(
    (itemData) =>
      dispatch({
        type: 'SET_POS_INTEGRATION_ITEM_DATA_MAPPINGS',
        payload: itemData,
      }),
  );
};

export const fetchPosIntegrationItemAdditionMappings = (
  storeId,
  posIntegrationId,
) => (dispatch) => {
  getPosIntegrationItemAdditionMappings(storeId, posIntegrationId).then(
    (additions) =>
      dispatch({
        type: 'SET_POS_INTEGRATION_ITEM_ADDITION_MAPPINGS',
        payload: additions,
      }),
  );
};

export const fetchPosIntegrationItemAdditionGroupMappings = (
  storeId,
  posIntegrationId,
) => (dispatch) => {
  getPosIntegrationItemAdditionGroupMappings(storeId, posIntegrationId).then(
    (additionGroups) =>
      dispatch({
        type: 'SET_POS_INTEGRATION_ITEM_ADDITION_GROUP_MAPPINGS',
        payload: additionGroups,
      }),
  );
};

export const fetchPosIntegrationTableMappings = (storeId, posIntegrationId) => (
  dispatch,
) => {
  getPosIntegrationTableMappings(storeId, posIntegrationId).then((tables) =>
    dispatch({
      type: 'SET_POS_INTEGRATION_TABLE_MAPPINGS',
      payload: tables,
    }),
  );
};

export const fetchStores = (
  recentStoreCreatedAt,
  oldestStoreCreatedAt,
  onSuccessCallback = () => undefined,
  showDeletedStores,
) => (dispatch) => {
  // v1 API filter format: array of strings "field||$op||value"
  const filters = [];
  if (recentStoreCreatedAt) {
    filters.push(`createdAt||$gt||${recentStoreCreatedAt}`);
  }
  if (oldestStoreCreatedAt) {
    filters.push(`createdAt||$lt||${oldestStoreCreatedAt}`);
  }
  const queryParams = {
    filter: filters.length > 0 ? filters : undefined,
    limit: 25,
    sort: 'createdAt,DESC',
  };

  getStores(queryParams, showDeletedStores).then((res) => {
    dispatch({
      type:
        recentStoreCreatedAt || oldestStoreCreatedAt
          ? 'SET_SPECIFIC_STORES'
          : 'SET_ALL_STORES',
      payload: res?.data,
    });
    dispatch({
      type: 'SET_NUMBER_OF_STORES',
      payload: res?.total,
    });
    onSuccessCallback(res?.data);
  });
};

export const fetchSpecificStores = (searchString, setIsSearching) => (
  dispatch,
) => {
  // v1 API filter format: array of strings "field||$op||value"
  const queryParams = {
    filter: [`name||$contL||${searchString}`],
    sort: [{ field: 'createdAt', order: 'DESC' }],
  };
  getStores(queryParams).then((stores) => {
    setIsSearching(false);
    dispatch({
      type: 'SET_SPECIFIC_STORES',
      payload: stores,
    });
  });
};

export const fetchAllStoresStats = () => (dispatch) => {
  const now = new Date();
  const firstDateOfPreviousMonth = getFirstDateOfMonth(now, -1);
  const firstDateOfCurrentMonth = getFirstDateOfMonth(now);
  const lastDateOfPreviousMonth = getLastDateOfMonth(now, -1);
  const lastDateOfCurrentMonth = getLastDateOfMonth(now);

  // v1 API filter format: array of strings "field||$op||value"
  const queryParamsThisMonth = {
    filter: [
      `createdAt||$gte||${firstDateOfCurrentMonth.toISOString()}`,
      `createdAt||$lte||${lastDateOfCurrentMonth.toISOString()}`,
    ],
  };
  const queryParamsLastMonth = {
    filter: [
      `createdAt||$gte||${firstDateOfPreviousMonth.toISOString()}`,
      `createdAt||$lte||${lastDateOfPreviousMonth.toISOString()}`,
    ],
  };

  Promise.all([
    getAllStoresStats(queryParamsThisMonth),
    getAllStoresStats(queryParamsLastMonth),
  ]).then(([thisMonthStats, lastMonthStats]) => {
    dispatch({
      type: 'SET_ALL_STORES_STATS',
      payload: {
        thisMonth: thisMonthStats,
        lastMonth: lastMonthStats,
      },
    });
  });
};

export const fetchAdditionGroups = (
  isAdminMode,
  menuId,
  id,
  storeId = null,
) => (dispatch) => {
  return getAdditionGroups(isAdminMode, menuId, id, storeId) // Return the promise
    .then((additionGroups) =>
      dispatch({
        type: 'SET_ADDITION_GROUPS',
        payload: additionGroups.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        ),
      }),
    );
};

export const fetchKiosks = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getKiosks({}, storeId).then((kiosks) =>
    dispatch({
      type: 'SET_KIOSKS',
      payload: kiosks,
    }),
  );
};

export const fetchPaymentTerminals = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getPaymentTerminals({}, storeId).then((terminals) =>
    dispatch({
      type: 'SET_PAYMENT_TERMINALS',
      payload: terminals,
    }),
  );
};

export const fetchAreas = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getAreas(storeId).then((areas) =>
    dispatch({
      type: 'SET_AREAS',
      payload: areas,
    }),
  );
};

export const fetchInvoices = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getInvoices({}, storeId).then((res) =>
    dispatch({
      type: 'SET_INVOICES',
      payload: res.data,
    }),
  );
};

export const fetchBridges = () => (dispatch, getState) => {
  const storeId = getState().store.store?.id;
  getBridges({}, storeId).then((bridges) =>
    dispatch({
      type: 'SET_BRIDGES',
      payload: bridges,
    }),
  );
};

export const fetchAllBridges = () => (dispatch) => {
  getAllBridges().then((bridges) =>
    dispatch({
      type: 'SET_ALL_BRIDGES',
      payload: bridges,
    }),
  );
};
